import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { confirmPasswordMatchValidator, customPasswordValidator } from '../../shared/utils/validators';
import { MatIconModule } from '@angular/material/icon'; 

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    MatCardModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    CommonModule,
    RouterModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss'
})
export class RegisterComponent {
  /** Register form */
  registerForm: FormGroup;

  /** Error message */
  errorMessage: string = "";

  /** Status on register form submission */
  isSubmitting: boolean = false;

   /** Toggle visibility */
  hidePassword = true;
  hideConfirmPassword = true;

  /** Security questions array */
  securityQuestions: string[] = [
    'In what city did you meet your spouse/significant other?',
    'What is the name of your favorite childhood friend?',
    'What street did you live on in third grade?',
    "What is your oldest sibling's birthday month and year? (e.g., January 1900)",
    'What is the middle name of your oldest child?',
    "What is your oldest sibling's middle name?",
    'What school did you attend for sixth grade?',
    'What was your childhood phone number including area code? (e.g., 000-000-0000)',
    "What is your oldest cousin's first and last name?",
    'What was the name of your first stuffed animal?',
    'In what city or town did your mother and father meet?',
    'Where were you when you had your first kiss?',
    'What is the first name of the boy or girl that you first kissed?',
    'What was the last name of your third grade teacher?',
    'In what city does your nearest sibling live?',
    "What is your oldest brother's birthday month and year? (e.g., January 1900)",
    "What is your maternal grandmother's maiden name?",
    'In what city or town was your first job?',
    'What is the name of the place your wedding reception was held?',
    "What is the name of a college you applied to but didn't attend?",
    'Where were you when you first heard about 9/11?'
  ];

  /**
   * Default constructor to build register form
   * @param fb 
   * @param _authService
   */
  constructor(
    private fb: FormBuilder,
    public _authService: AuthService,
    public _router: Router,
  ) {
    this.registerForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(6)]],
      password: ['', customPasswordValidator],
      confirmPassword: ['', [Validators.required, confirmPasswordMatchValidator]],
      securityQuestion1: ['', Validators.required],
      answer1: ['', Validators.required],
      securityQuestion2: ['', Validators.required],
      answer2: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      dateOfBirth: ['', Validators.required],
    });
  }

  /**
   * Handles form submission
   */
  onSubmit(): void {
    if (!this.registerForm.valid || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    const formValue = this.registerForm.value;

    this._authService.register(formValue).subscribe({
      // On successful register, log the success message and route to home
      next: () => {
        console.log('[REGISTER]: Register successful');
        this._router.navigate(['/']);
        this.isSubmitting = false;
      },
      // On error, log the error message and display an error to the user
      error: (error: string) => {
        console.error('[REGISTER]: Register failed:', error);
        this.errorMessage = error || 'Registration failed. Please try again.';
        this.isSubmitting = false;
      }
    });
  }



  /**
   * Formats the date input as MM/DD/YYYY with slashes as the user types, preserving user input
   * @param event - The input event from the date of birth field
   */
  onDateOfBirthInput(event: Event): void {
    // Get the value as only numbers
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9]/g, '');

    // Add slashes to the date input
    if (value.length > 2 && value.length <= 4) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    } else if (value.length > 4) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4, 8);
    }
    // Only update if changed, to avoid cursor jump and clearing
    if (input.value !== value) {
      input.value = value;
    }
  }

}
